/**
 * ArtDirectorPanel — Phase 3
 *
 * Inline panel inside V2CardDrawer (Hero image section). Drives the REAL
 * Art Director pipeline:
 *   - "Suggest" → POST /art-director/recommend  (retrieval → heuristics → LLM)
 *   - "Use this" → POST /art-director/decisions/{recommendation_id}/commit
 *                  (writes the audit/learning row), then reflects the pick
 *                  on the card's hero image.
 *
 * State machine:
 *   idle ──"Suggest"──► loading ─► recommending ──"Use this"──► committing ─► committed
 *                                                ──"Reject all"──► idle
 *                                                ──card edits──► (stale=true)
 *
 * No demo/canned source is reachable from this panel — the legacy GET
 * canned endpoint is no longer called.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CachedImage } from "./CachedImage";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Loader2, Wand2, RefreshCcw, ChevronDown, ChevronUp, AlertTriangle, Check, X } from "lucide-react";
import { toast } from "sonner";

const _API_BASE = process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");
const API = `${_API_BASE}/api`;

const TREATMENT_LABELS = {
  resiligent_institutional: "Institutional",
  remi_warm: "Warm personal",
  conceptual_mono: "Conceptual mono",
  stock_natural: "Stock scene",
};

const DECISION_PATH_BLURBS = {
  heuristic: "Picked by rule",
  llm: "Picked by LLM",
  hybrid: "Rule weak → LLM ranked",
  llm_no_fit: "LLM found no fit",
  llm_parse_failed: "LLM response failed parse — fell back",
  rule_5_existing_outside_topk: "Your pick is outside top-20",
  router_stock: "Routed to live stock",
};

const IMAGE_KIND_LABELS = {
  person_remi: "Photo of Remi",
  person_institutional: "Institutional / people",
  scene_stock: "Stock scene",
};


// ─── Stale-detection: which card fields invalidate a recommendation? ──
// Spec A2: any edit to title / body / voice / market / image should mark
// the panel stale so the operator can re-suggest. Pure field-tracking
// keeps us out of the card-edit drawer's mutation paths.
function _staleSignature(card) {
  if (!card) return null;
  return JSON.stringify({
    title: card.title || "",
    body_md: card.body_md || "",
    voice_key: card.voice_key || "",
    market_key: card.market_key || "",
    image_url: card.image_url || "",
  });
}


// ─── One pick (primary or alternate) ──────────────────────────────────
function PickCard({ pick, label, onUse, operatorDecision, disabled, testIdPrefix }) {
  if (!pick) return null;
  return (
    <div
      className="rounded-md border border-slate-200 bg-white p-2.5 flex gap-2.5"
      data-testid={`${testIdPrefix}-card`}
    >
      <CachedImage
        src={pick.asset_thumbnail_url}
        alt={pick.asset_file_name || pick.asset_id}
        className="w-20 h-20 object-cover rounded shrink-0 border border-slate-200 bg-slate-50"
        onError={(e) => {
          e.currentTarget.style.opacity = "0.3";
          e.currentTarget.alt = "[thumbnail unavailable]";
        }}
        data-testid={`${testIdPrefix}-thumb`}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-indigo-100 text-indigo-900 border-indigo-300 text-[10px]">
            {label}
          </Badge>
          <Badge
            variant="outline"
            className="border-slate-300 text-slate-700 text-[10px]"
            data-testid={`${testIdPrefix}-treatment`}
          >
            {TREATMENT_LABELS[pick.treatment_profile_key] || pick.treatment_profile_key}
          </Badge>
          <span
            className="text-[10px] text-slate-500"
            data-testid={`${testIdPrefix}-confidence`}
          >
            {Math.round(pick.confidence * 100)}% confidence
          </span>
        </div>
        <p
          className="text-[11px] text-slate-700 leading-snug"
          data-testid={`${testIdPrefix}-reasoning`}
        >
          <span className="font-semibold text-slate-500">Why this pick: </span>
          {pick.reasoning}
        </p>
        <p
          className="text-[10px] text-slate-400 truncate"
          data-testid={`${testIdPrefix}-filename`}
        >
          {pick.asset_file_name || pick.asset_id.slice(0, 8)}
        </p>
        <div className="pt-0.5">
          <Button
            size="sm"
            className="h-6 px-2 text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={() => onUse(pick, operatorDecision)}
            disabled={disabled}
            data-testid={`${testIdPrefix}-use-btn`}
          >
            Use this
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─── Matched-signals + rejected-candidates disclosure ─────────────────
function ExplainMore({ envelope }) {
  const [open, setOpen] = useState(false);
  const matched = envelope.primary?.matched_signals || [];
  const rejected = envelope.rejected_candidates || [];
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50/50 p-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-[10px] text-slate-600 hover:text-slate-900"
        data-testid="art-director-explain-toggle"
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        Explain more
      </button>
      {open && (
        <div className="mt-1.5 space-y-2" data-testid="art-director-explain-body">
          {matched.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-slate-700">Matched signals</p>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {matched.map((s, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="border-slate-300 text-slate-600 text-[9px]"
                    data-testid={`art-director-signal-${i}`}
                  >
                    {s}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {rejected.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-slate-700">
                Considered but dropped ({rejected.length})
              </p>
              <ul className="mt-0.5 space-y-0.5">
                {rejected.map((r, i) => (
                  <li
                    key={i}
                    className="text-[10px] text-slate-600"
                    data-testid={`art-director-rejected-${i}`}
                  >
                    <span className="font-mono text-slate-400">
                      {(r.file_name || r.asset_id || "asset").slice(0, 32)}
                    </span>{" "}
                    — {r.reason_dropped}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Main panel ───────────────────────────────────────────────────────
export default function ArtDirectorPanel({ workspaceId, cardId, card, onPickCommitted }) {
  // State machine: idle | loading | recommending | committing | committed
  const [state, setState] = useState("idle");
  const [envelope, setEnvelope] = useState(null);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(false);

  // P1 variety: session-scoped memory of own-photo asset_ids already shown
  // for THIS card. Empty → first suggest (genuine best match); non-empty →
  // re-suggest rotates among the co-fit band. Resets on card change / reject
  // (so it never paints itself into a corner across visits).
  const [shownAssetIds, setShownAssetIds] = useState([]);

  // Stale banner: any tracked card-field change after a recommendation
  // is loaded marks the envelope stale.
  const lastSigRef = useRef(null);
  const [stale, setStale] = useState(false);

  // Stale detection — runs whenever the card's tracked fields change.
  // Note on the race: lastSigRef is set on suggest()-success only. This
  // effect compares the *current* card signature against the snapshot
  // taken at the last successful recommend; it never overwrites the
  // snapshot itself (that would defeat the stale flag if the card
  // changed during the API round-trip).
  useEffect(() => {
    const sig = _staleSignature(card);
    if (envelope && lastSigRef.current && sig !== lastSigRef.current) {
      setStale(true);
    }
  }, [card, envelope]);

  // Reset the per-card variety memory whenever the card itself changes.
  // Session-scoped, not permanent — a card revisited later starts fresh.
  useEffect(() => {
    setShownAssetIds([]);
  }, [cardId]);

  const suggest = async () => {
    setState("loading");
    setError(null);
    setAuthError(false);
    // First suggest (no memory) → genuine best match (use server cache).
    // Re-suggest (memory present) → bypass cache + send already-shown ids so
    // the backend rotates among the co-fit band.
    const isResuggest = shownAssetIds.length > 0;
    try {
      // REAL pipeline: retrieval → heuristics → LLM fallback.
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/art-director/recommend`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            card_id: cardId,
            use_cached: !isResuggest,
            exclude_asset_ids: isResuggest ? shownAssetIds : [],
          }),
        }
      );
      if (!r.ok) {
        if (r.status === 401 || r.status === 403) {
          const ae = new Error("Your session has expired. Refresh the page (or log in again), then try Suggest.");
          ae.isAuth = true;
          throw ae;
        }
        const e = await r.json().catch(() => ({}));
        throw new Error(e?.detail || `Recommend failed (${r.status})`);
      }
      const body = await r.json();
      setEnvelope(body);
      // Remember the own-photo we just showed so the next re-suggest rotates.
      const shownId = body?.primary?.asset_id;
      if (shownId) {
        setShownAssetIds((prev) => (prev.includes(shownId) ? prev : [...prev, shownId]));
      }
      lastSigRef.current = _staleSignature(card);
      setStale(false);
      setState("recommending");
    } catch (e) {
      setError(String(e?.message || e));
      setAuthError(!!e?.isAuth);
      setState("idle");
      toast.error(String(e?.message || e));
    }
  };

  const commitPick = async (pick, operatorDecision) => {
    if (!pick || !operatorDecision) return;
    const recId = envelope?.recommendation_id;
    if (!recId) {
      toast.error("Missing recommendation id — re-suggest first");
      return;
    }
    setState("committing");
    try {
      // 1. REAL commit — writes the audit/learning row and persists the
      //    chosen treatment to the card server-side. Returns 204.
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/art-director/decisions/${recId}/commit`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ operator_decision: operatorDecision }),
        }
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e?.detail || `Commit failed (${r.status})`);
      }
      // 2. Reflect the pick on the card's hero image immediately via the
      //    dedicated select-candidate endpoint. Stock picks carry an
      //    asset_full_url (provider CDN) — use it as-is; own-photos derive
      //    the /full URL from the /thumbnail endpoint. Best-effort: the
      //    audit row is already written, so a mirror failure must not block.
      const heroUrl =
        pick.asset_full_url ||
        (pick.asset_thumbnail_url || "").replace("/thumbnail", "/full") ||
        pick.asset_thumbnail_url;
      try {
        const mr = await fetch(
          `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/select-candidate`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ image_url: heroUrl }),
          }
        );
        if (!mr.ok) {
          console.warn("Art Director: hero mirror failed", mr.status);
        }
      } catch (mirrorErr) {
        console.warn("Art Director: hero mirror error", mirrorErr);
      }
      setState("committed");
      toast.success("Pick committed to card");
      onPickCommitted && onPickCommitted({ ...pick, asset_thumbnail_url: heroUrl });
    } catch (e) {
      setState("recommending");
      toast.error(String(e?.message || e));
    }
  };

  const rejectAll = () => {
    setEnvelope(null);
    setState("idle");
    setStale(false);
    setError(null);
    setShownAssetIds([]);
  };

  // Pretty path label
  const pathBlurb = envelope?.decision_path
    ? DECISION_PATH_BLURBS[envelope.decision_path] || envelope.decision_path
    : null;

  return (
    <div
      className="rounded-md border border-indigo-200 bg-indigo-50/40 p-3 space-y-2"
      data-testid="art-director-panel"
    >
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold text-indigo-900 flex items-center gap-1.5">
          <Wand2 className="h-3.5 w-3.5" /> Art Director
        </h4>
      </div>

      {state === "idle" && (
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-slate-700">
            Let Art Director suggest a photo + treatment for this card.
          </p>
          <Button
            size="sm"
            onClick={suggest}
            className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="art-director-suggest-btn"
          >
            <Wand2 className="h-3 w-3 mr-1" /> Suggest
          </Button>
        </div>
      )}

      {state === "loading" && (
        <div
          className="flex items-center gap-2 text-[11px] text-slate-700"
          data-testid="art-director-loading"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Art Director is thinking…
        </div>
      )}

      {error && state === "idle" && (
        authError ? (
          <div className="flex items-center justify-between gap-2 rounded bg-amber-50 border border-amber-200 px-2 py-1.5" data-testid="art-director-error">
            <p className="text-[10px] text-amber-800">{error}</p>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[10px] shrink-0"
              onClick={() => window.location.reload()}
              data-testid="art-director-refresh-btn"
            >
              Refresh
            </Button>
          </div>
        ) : (
          <p className="text-[10px] text-red-700" data-testid="art-director-error">
            {error}
          </p>
        )
      )}

      {envelope && (state === "recommending" || state === "committing" || state === "committed") && (
        <div className="space-y-2" data-testid="art-director-result">
          {/* Stale banner — A2. Suppressed once a pick is committed (the
              recommendation has been acted on, so staleness is moot). */}
          {stale && state === "recommending" && (
            <div
              className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 p-2"
              data-testid="art-director-stale-banner"
            >
              <AlertTriangle className="h-3.5 w-3.5 text-amber-700 shrink-0" />
              <p className="text-[11px] text-amber-900 flex-1">
                Card changed since this recommendation — results may be out of date.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-[10px]"
                onClick={suggest}
                data-testid="art-director-resuggest-btn"
              >
                <RefreshCcw className="h-3 w-3 mr-1" /> Re-suggest
              </Button>
            </div>
          )}

          {/* Router decision — VISIBLE, never hidden. Lets a wrong route be
              diagnosed: intent classification vs query derivation. */}
          {envelope.routing && (
            <div
              className="rounded-md bg-violet-50 border border-violet-200 p-2 text-[10px] space-y-1"
              data-testid="art-director-routing"
            >
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-semibold text-violet-900">Designer routed to:</span>
                <Badge
                  className="bg-violet-200 text-violet-900 border-violet-300 text-[10px]"
                  data-testid="art-director-routing-kind"
                >
                  {IMAGE_KIND_LABELS[envelope.routing.image_kind] || envelope.routing.image_kind}
                </Badge>
                <span className="text-violet-700">
                  ({envelope.routing.confidence} confidence)
                </span>
                {envelope.routing.failure_type && (
                  <Badge
                    className="bg-amber-100 text-amber-900 border-amber-300 text-[9px]"
                    data-testid="art-director-routing-fallback"
                  >
                    fallback: {envelope.routing.failure_type}
                  </Badge>
                )}
              </div>
              {envelope.routing.image_kind === "scene_stock" && envelope.routing.stock_query && (
                <div className="text-violet-800" data-testid="art-director-routing-query">
                  Searched live stock for: <span className="font-mono font-medium">"{envelope.routing.stock_query}"</span>
                </div>
              )}
              {envelope.routing.reasoning && (
                <div className="text-violet-700 italic" data-testid="art-director-routing-reasoning">
                  {envelope.routing.reasoning}
                </div>
              )}
            </div>
          )}

          {/* Decision path — RAW token leads (diagnostic, not prettied);
              human blurb is clearly secondary. */}
          <div className="flex items-center gap-2 flex-wrap">
            {envelope.decision_path && (
              <Badge
                className="bg-indigo-100 text-indigo-900 border-indigo-300 text-[10px]"
                data-testid="art-director-path-badge"
              >
                <span className="font-mono font-semibold" data-testid="art-director-decision-path-raw">
                  {envelope.decision_path}
                </span>
                {pathBlurb && pathBlurb !== envelope.decision_path && (
                  <span className="ml-1 font-normal text-indigo-700/90">— {pathBlurb}</span>
                )}
                {envelope.heuristic_rule_name && (
                  <span className="ml-1 text-indigo-700">
                    · {envelope.heuristic_rule_name}
                  </span>
                )}
              </Badge>
            )}
            {envelope.llm_cost_usd != null && envelope.llm_cost_usd > 0 && (
              <span
                className="text-[10px] text-slate-500"
                data-testid="art-director-llm-cost"
              >
                LLM cost ${envelope.llm_cost_usd.toFixed(4)}
              </span>
            )}
            <span
              className="text-[10px] text-slate-500"
              data-testid="art-director-pool-stats"
            >
              {envelope.pool_stats?.candidates_considered} considered of{" "}
              {envelope.pool_stats?.candidates_total_matched}
            </span>
            {envelope.cofit_alternates > 1 && (
              <span
                className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                data-testid="art-director-cofit-alternates"
                title="Photos that fit this card about equally well. Re-suggest rotates among these genuine alternates — it never reaches for a weaker match."
              >
                {envelope.cofit_alternates} co-fitting alternates · re-suggest to rotate
              </span>
            )}
          </div>

          {/* Primary pick OR no-fit message */}
          {envelope.primary ? (
            <PickCard
              pick={envelope.primary}
              label="Primary"
              onUse={commitPick}
              operatorDecision="approved_primary"
              disabled={state === "committing"}
              testIdPrefix="art-director-primary"
            />
          ) : (
            <div
              className="rounded-md bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-700"
              data-testid="art-director-no-fit"
            >
              {envelope.decision_path === "router_stock"
                ? `No stock scene found for "${envelope.routing?.stock_query || "this card"}". Try editing the card or generating one with Rex above.`
                : "Art Director couldn't find a photo in your library that fits this card's framing. Try generating one with Rex above, or upload manually."}
            </div>
          )}

          {/* Alternates */}
          {envelope.alternates && envelope.alternates.length > 0 && (
            <div className="space-y-1.5" data-testid="art-director-alternates">
              <p className="text-[10px] font-semibold text-slate-700">
                Alternates ({envelope.alternates.length})
              </p>
              {envelope.alternates.map((alt, i) => (
                <PickCard
                  key={alt.asset_id}
                  pick={alt}
                  label={`Alt ${i + 1}`}
                  onUse={commitPick}
                  operatorDecision={`approved_alternate_${i + 1}`}
                  disabled={state === "committing"}
                  testIdPrefix={`art-director-alt-${i}`}
                />
              ))}
            </div>
          )}

          {/* Explain-more disclosure */}
          <ExplainMore envelope={envelope} />

          {/* Footer actions */}
          <div className="flex items-center justify-between pt-1">
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[10px]"
              onClick={rejectAll}
              disabled={state === "committing"}
              data-testid="art-director-reject-all-btn"
            >
              <X className="h-3 w-3 mr-1" /> Reject all
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[10px]"
              onClick={suggest}
              disabled={state === "committing"}
              data-testid="art-director-resuggest-btn-footer"
            >
              <RefreshCcw className="h-3 w-3 mr-1" /> Re-suggest
            </Button>
          </div>

          {state === "committing" && (
            <p
              className="text-[10px] text-slate-600 flex items-center gap-1"
              data-testid="art-director-committing"
            >
              <Loader2 className="h-3 w-3 animate-spin" /> Committing pick to card…
            </p>
          )}

          {state === "committed" && (
            <p
              className="text-[10px] text-emerald-700 flex items-center gap-1 font-semibold"
              data-testid="art-director-committed"
            >
              <Check className="h-3 w-3" /> Pick committed.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
